import type { JSX } from 'react';
import { Controller } from 'react-hook-form';
import type { WireUser } from '@logistics/contracts';
import { cn } from '@/lib/utils';
import { fieldLabel, segment } from '../shell/look';
import { useUsers } from '../users/use-users';
import type { EditorForm } from './schedule-editor';
import { toggled } from './schedule-form';

const CHANNELS = [
  { value: 'telegram', label: 'Telegram message' },
  { value: 'web', label: 'New web chat' },
] as const;

/** Why a person would not receive it on the chosen channels, or nothing. */
function gap(user: WireUser, channels: readonly string[]): string | undefined {
  const telegram = user.channels.includes('telegram') && user.telegramId !== undefined;
  const web = user.channels.includes('web');
  const reached = channels.some((c) => (c === 'telegram' ? telegram : web));
  if (reached) return undefined;
  return channels.includes('telegram') && !telegram ? 'no Telegram' : 'no web access';
}

/** How it is delivered, and to whom: everybody, or exactly the people ticked. */
export function WhoFields({ form }: { form: EditorForm }): JSX.Element {
  const audience = form.watch('audience');
  const channels = form.watch('channels');
  return (
    <>
      <Controller
        control={form.control}
        name="channels"
        render={({ field }) => (
          <div className={fieldLabel} role="group" aria-label="Deliver as">
            Deliver as
            <div className="flex flex-wrap gap-4">
              {CHANNELS.map((c) => (
                <label key={c.value} className="text-ink flex items-center gap-2 text-[13.5px]">
                  <input
                    type="checkbox"
                    checked={field.value.includes(c.value)}
                    onChange={() => field.onChange(toggled(field.value, c.value))}
                  />
                  {c.label}
                </label>
              ))}
            </div>
          </div>
        )}
      />
      <div className={fieldLabel}>
        Send to
        <div className={cn(segment.group, 'bg-surf2 w-fit')} role="radiogroup" aria-label="Send to">
          {(['all', 'users'] as const).map((a) => (
            <button
              key={a}
              type="button"
              role="radio"
              aria-checked={audience === a}
              onClick={() => form.setValue('audience', a, { shouldDirty: true })}
              className={audience === a ? segment.on : segment.off}
            >
              {a === 'all' ? 'All users' : 'Selected users'}
            </button>
          ))}
        </div>
      </div>
      {audience === 'users' && <UserPicker form={form} channels={channels} />}
    </>
  );
}

function UserPicker({
  form,
  channels,
}: {
  form: EditorForm;
  channels: readonly string[];
}): JSX.Element {
  const users = useUsers(true);
  const enabled = (users.data ?? []).filter((u) => u.enabled);
  return (
    <Controller
      control={form.control}
      name="userIds"
      render={({ field }) => (
        <div
          role="group"
          aria-label="Recipients"
          className="border-line bg-surf2 flex max-h-56 flex-col overflow-y-auto rounded-[9px] border"
        >
          {users.isPending && <div className="text-mute p-3 text-[13px]">Loading users…</div>}
          {enabled.map((user) => {
            const missing = gap(user, channels);
            return (
              <label
                key={user.id}
                className="border-line flex items-center gap-2.5 border-b px-3 py-2 text-[13.5px] last:border-b-0"
              >
                <input
                  type="checkbox"
                  checked={field.value.includes(user.id)}
                  onChange={() => field.onChange(toggled(field.value, user.id))}
                />
                <span className="min-w-0 flex-1 truncate">{user.name}</span>
                {missing !== undefined && (
                  <span className="text-warn font-mono text-[11px]">{missing}</span>
                )}
              </label>
            );
          })}
        </div>
      )}
    />
  );
}
